import {
  BILLING_SETTINGS_SOURCE_CUSTOM,
  BILLING_SETTINGS_SOURCE_TRAINER,
  resolveBillingSettingsTrainerForRecipient,
} from "./billing-recipient-semantics.js";
import {
  normalizeTrainerRatePeriod,
  parseTrainerPeriodRatesForSave,
  parseFlatStandardRatesForSave,
  resolveEffectiveTrainerRateRow,
} from "./trainer-rate-periods.js";
import {
  computeTrainerSessionHourlyRate,
  getTrainerStandardRatesForDate,
  getRateColumnForSelection,
  parseStandardRatePeriods,
  resolveGroupRateFromRow,
} from "./trainer-session-rate.js";

export const PARTICIPANT_RATE_SETTINGS_SOURCE_TRAINER =
  BILLING_SETTINGS_SOURCE_TRAINER;
export const PARTICIPANT_RATE_SETTINGS_SOURCE_CUSTOM =
  BILLING_SETTINGS_SOURCE_CUSTOM;

function asStr(v) {
  return String(v ?? "").trim();
}

function asNum(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function standardRatePeriodsFromTrainer(trainer) {
  const tr = trainer && typeof trainer === "object" ? trainer : {};
  const arr = tr.standard_rate_periods ?? tr.standardRatePeriods;
  if (Array.isArray(arr) && arr.length > 0) return arr;
  const hourly = tr.hourly_rates;
  if (hourly && typeof hourly === "object" && Object.keys(hourly).length) {
    return [{ valid_from: "2026-01-01", valid_to: null, rates: hourly }];
  }
  return [];
}

function collectRateGroupIdsFromPeriods(periodsList) {
  const set = new Set(["youth", "adults"]);
  for (const periods of periodsList) {
    for (const p of Array.isArray(periods) ? periods : []) {
      const rates = p?.rates;
      if (rates && typeof rates === "object") {
        for (const k of Object.keys(rates)) {
          const id = asStr(k);
          if (id) set.add(id);
        }
      }
    }
  }
  return [...set];
}

/**
 * Kanonische Form wie nach Speichern — verhindert False Positives durch UI-Normalisierung.
 *
 * @param {unknown} raw
 * @param {string[]} [groupIds]
 */
export function normalizeStandardRatePeriodsForCompare(raw, groupIds = []) {
  const arr = Array.isArray(raw) ? raw : [];
  const ids =
    Array.isArray(groupIds) && groupIds.length
      ? groupIds.filter(Boolean)
      : collectRateGroupIdsFromPeriods([arr]);
  return arr
    .map((p) => {
      const normalized = normalizeTrainerRatePeriod(
        p,
        ids,
        asStr(p?.standard_target_group_id) || ids[0] || "youth",
      );
      return {
        valid_from: normalized.valid_from,
        valid_to: normalized.valid_to ? normalized.valid_to : null,
        standard_target_group_id: normalized.standard_target_group_id,
        apply_standard_to_other_groups:
          normalized.apply_standard_to_other_groups !== false,
        flat_standard_rates: parseFlatStandardRatesForSave(
          normalized.flat_standard_rates,
        ),
        rates: parseTrainerPeriodRatesForSave(normalized.rates, ids),
      };
    })
    .filter((p) => p.valid_from)
    .sort((a, b) => a.valid_from.localeCompare(b.valid_from));
}

/**
 * @param {unknown} trainerBefore
 * @param {unknown} trainerAfter
 */
export function trainerParticipantRateSettingsSemanticallyChanged(
  trainerBefore,
  trainerAfter,
) {
  const before =
    trainerBefore && typeof trainerBefore === "object" ? trainerBefore : {};
  const after =
    trainerAfter && typeof trainerAfter === "object" ? trainerAfter : {};
  const beforePeriods = standardRatePeriodsFromTrainer(before);
  const afterPeriods = standardRatePeriodsFromTrainer(after);
  const ids = collectRateGroupIdsFromPeriods([beforePeriods, afterPeriods]);
  const beforeKey = JSON.stringify(
    normalizeStandardRatePeriodsForCompare(beforePeriods, ids),
  );
  const afterKey = JSON.stringify(
    normalizeStandardRatePeriodsForCompare(afterPeriods, ids),
  );
  return beforeKey !== afterKey;
}

/**
 * @param {unknown} trainer
 * @param {string} atDateIso
 */
export function trainerParticipantRatePeriodsAtDate(trainer, atDateIso) {
  const effectiveFrom = asStr(
    trainer?.participant_rate_settings_effective_from,
  ).slice(0, 10);
  const previous = trainer?.participant_rate_settings_previous;
  const date = asStr(atDateIso).slice(0, 10);
  if (
    effectiveFrom &&
    /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) &&
    date &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    date < effectiveFrom &&
    previous &&
    typeof previous === "object" &&
    Array.isArray(previous.standard_rate_periods)
  ) {
    return previous.standard_rate_periods;
  }
  return trainer?.standard_rate_periods ?? trainer?.standardRatePeriods ?? [];
}

/**
 * @param {unknown} trainer
 * @param {string} [atDateIso]
 */
export function extractTrainerDefaultParticipantRates(trainer, atDateIso = "") {
  const date =
    asStr(atDateIso).slice(0, 10) || new Date().toISOString().slice(0, 10);
  const periodsRaw = trainerParticipantRatePeriodsAtDate(trainer, date);
  const trainerView = {
    ...(trainer && typeof trainer === "object" ? trainer : {}),
    standard_rate_periods: periodsRaw,
  };
  const period = getTrainerStandardRatesForDate(trainerView, date);
  if (!period) {
    return { single: null, group: null };
  }
  const tg =
    asStr(period.standard_target_group_id) ||
    asStr(parseStandardRatePeriods(trainerView)[0]?.standard_target_group_id) ||
    "youth";
  const row = resolveEffectiveTrainerRateRow(period, tg);
  if (!row) return { single: null, group: null };
  return {
    single: asNum(row.single),
    group: asNum(resolveGroupRateFromRow(row) ?? row.group),
  };
}

/**
 * @param {{ person?: object|null, trainers?: object[], participantRateTrainerId?: string, billingSettingsTrainerId?: string, coordinatorTrainerId?: string }} input
 */
export function resolveParticipantRateSettingsTrainerForRecipient(input) {
  const trainers = Array.isArray(input?.trainers) ? input.trainers : [];
  const byId = new Map(trainers.map((tr) => [asStr(tr?.id), tr]));
  const fromPerson = asStr(input?.person?.participant_rate_trainer_id);
  if (fromPerson && byId.has(fromPerson)) return byId.get(fromPerson);
  const explicit = asStr(input?.participantRateTrainerId);
  if (explicit && byId.has(explicit)) return byId.get(explicit);
  return resolveBillingSettingsTrainerForRecipient(input);
}

/**
 * @param {unknown} person
 * @param {unknown} [trainer]
 */
export function isRecipientParticipantRateCustomized(person, trainer = null) {
  const source = asStr(person?.participant_rate_settings_source);
  if (source === PARTICIPANT_RATE_SETTINGS_SOURCE_CUSTOM) return true;
  if (source === PARTICIPANT_RATE_SETTINGS_SOURCE_TRAINER) return false;
  if (!trainer) return false;
  const defaults = extractTrainerDefaultParticipantRates(trainer);
  const personSingle = asNum(person?.participant_rate_single);
  const personGroup = asNum(person?.participant_rate_group);
  if (
    personSingle != null &&
    defaults.single != null &&
    personSingle !== defaults.single
  ) {
    return true;
  }
  if (
    personGroup != null &&
    defaults.group != null &&
    personGroup !== defaults.group
  ) {
    return true;
  }
  return false;
}

/**
 * @param {unknown} trainer
 */
export function buildTrainerParticipantRateDefaultsForRecipient(trainer) {
  const trainerId = asStr(trainer?.id) || null;
  const rates = extractTrainerDefaultParticipantRates(trainer);
  return {
    participant_rate_single: rates.single,
    participant_rate_group: rates.group,
    participant_rate_settings_source: PARTICIPANT_RATE_SETTINGS_SOURCE_TRAINER,
    participant_rate_trainer_id: trainerId,
  };
}

/**
 * @param {Record<string, unknown>} payload
 * @param {unknown} trainer
 */
export function applyTrainerParticipantRateDefaultsToPersonPayload(
  payload,
  trainer,
) {
  const base = payload && typeof payload === "object" ? { ...payload } : {};
  return {
    ...base,
    ...buildTrainerParticipantRateDefaultsForRecipient(trainer),
  };
}

/**
 * @param {unknown} person
 * @param {unknown} trainer
 * @param {{ atDate?: string, targetGroup?: string, trainingType?: string, groupSize?: number|string }} [opts]
 */
export function resolveEffectiveParticipantHourlyRate(
  person,
  trainer,
  opts = {},
) {
  const trainingType = asStr(opts.trainingType) || "single";
  const groupSize = opts.groupSize;
  const col = getRateColumnForSelection(trainingType, groupSize);

  if (
    asStr(person?.participant_rate_settings_source) ===
    PARTICIPANT_RATE_SETTINGS_SOURCE_CUSTOM
  ) {
    if (col === "single") {
      const single = asNum(person?.participant_rate_single);
      if (single != null) return single;
    } else if (col === "group") {
      const group = asNum(person?.participant_rate_group);
      if (group != null) return group;
    }
  }

  const atDate = asStr(opts.atDate).slice(0, 10);
  const periodsRaw = trainerParticipantRatePeriodsAtDate(trainer, atDate);
  const trainerView = {
    ...(trainer && typeof trainer === "object" ? trainer : {}),
    standard_rate_periods: periodsRaw,
  };
  return computeTrainerSessionHourlyRate(trainerView, {
    atDate,
    targetGroup: opts.targetGroup,
    trainingType: opts.trainingType,
    groupSize: opts.groupSize,
  });
}

/**
 * @param {Record<string, unknown>} payload
 * @param {Record<string, unknown>} existing
 * @param {Record<string, unknown>} trainerDefaults
 */
export function markRecipientParticipantRatePayload(
  payload,
  existing,
  trainerDefaults = {},
) {
  const next = payload && typeof payload === "object" ? { ...payload } : {};
  const prev = existing && typeof existing === "object" ? existing : {};
  const touched =
    ("participant_rate_single" in next &&
      asNum(next.participant_rate_single) !==
        asNum(prev.participant_rate_single)) ||
    ("participant_rate_group" in next &&
      asNum(next.participant_rate_group) !==
        asNum(prev.participant_rate_group));
  if (!touched) return next;

  const single = asNum(next.participant_rate_single);
  const group = asNum(next.participant_rate_group);
  const trainerSingle = asNum(trainerDefaults.participant_rate_single);
  const trainerGroup = asNum(trainerDefaults.participant_rate_group);
  const exactTrainerMatch =
    single === trainerSingle &&
    group === trainerGroup &&
    trainerSingle != null &&
    trainerGroup != null;
  if (exactTrainerMatch) {
    next.participant_rate_settings_source =
      PARTICIPANT_RATE_SETTINGS_SOURCE_TRAINER;
    if (trainerDefaults.participant_rate_trainer_id) {
      next.participant_rate_trainer_id =
        trainerDefaults.participant_rate_trainer_id;
    }
    return next;
  }
  next.participant_rate_settings_source =
    PARTICIPANT_RATE_SETTINGS_SOURCE_CUSTOM;
  return next;
}
